import React from 'react';

import type { AttachmentMetadataDTO } from '@/services/ws-funnel/src/main/resources/rest-api';
import { getAttachmentContentById } from '@/services/ws-funnel/src/main/resources/rest-api';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { Table } from '@/js/libs/ui';
import { useLazyRequest } from '@/js/libs/use-request';

import { saveAttachmentFile } from 'src/utils/saveAttachmentFile';
import { AttachmentRow } from './AttachmentRow';
import type { UploadFile } from './types';
import { UploadFileRow } from './UploadFileRow';

interface AttachmentsProps {
  templateAttachments: AttachmentMetadataDTO[];
  setTemplateAttachments: React.Dispatch<React.SetStateAction<AttachmentMetadataDTO[]>>;
  filesToUpload?: UploadFile[];
  setFilesToUpload?: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  uploading: boolean;
}

export const Attachments: React.FC<AttachmentsProps> = React.memo(function Attachments({
  templateAttachments,
  setTemplateAttachments,
  filesToUpload,
  setFilesToUpload,
  uploading,
}) {
  const { load: loadAttachmentContentById } = useLazyRequest({
    apiMethod: getAttachmentContentById,
  });

  const handleTemplateAttachmentDelete = useCallbackRef((attachment: AttachmentMetadataDTO) => {
    setTemplateAttachments((files) => files.filter((f) => f.id !== attachment.id));
  });

  const handleTemplateAttachmentDownload = useCallbackRef(async (attachment: AttachmentMetadataDTO) => {
    const { data } = await loadAttachmentContentById({ attachmentId: attachment.id });
    saveAttachmentFile(data);
  });

  const handleUploadFileDelete = useCallbackRef((file: UploadFile) => {
    setFilesToUpload?.((files) => files.filter((f) => f.id !== file.id));
  });

  if (!templateAttachments.length && !filesToUpload?.length) {
    return null;
  }

  return (
    <Table>
      <tbody>
        {templateAttachments.map((attachment) => (
          <AttachmentRow
            key={attachment.id.toString()}
            attachment={attachment}
            onDelete={handleTemplateAttachmentDelete}
            onDownload={handleTemplateAttachmentDownload}
          />
        ))}
        {filesToUpload?.map((file) => (
          <UploadFileRow key={file.id} uploadFile={file} onDelete={handleUploadFileDelete} uploading={uploading} />
        ))}
      </tbody>
    </Table>
  );
});
